/* eslint-disable class-methods-use-this */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classnames from 'classnames';
import { isEdge, isSafari } from 'react-device-detect';

import { Spinner, Pagination } from '@breezeway/brzw-components';

import emptySearchImage from './assets/no-results-empty.svg';
import emptyFilteredImage from './assets/no-results-filters.svg';
import errorImage from './assets/server-error-empty.svg';
import FlexAutoTableRow from './row';

import FlexTableHeader from './header';
import EmptyContentMessage from '../empty_content_message';
import { PAGINATION_ARROWS, PAGINATION_TYPES } from './constants';

import './flex_table.scss';

const CLASS_NAME = 'flex-table';

// eslint-disable-next-line react/prop-types
const renderEmptyMessage = ({ buttonText, onClick, title, subTitle, imageSrc }) => (
  <EmptyContentMessage
    buttonText={buttonText}
    className={`${CLASS_NAME}__empty-message`}
    imageSrc={imageSrc}
    onClick={onClick}
    subTitle={subTitle}
    title={title}
    withoutIcon
  />
);

const renderEmptySearchedMessage = () => (
  <EmptyContentMessage
    className={`${CLASS_NAME}__empty-message`}
    imageSrc={emptySearchImage}
    subTitle="Try adjusting your filters or search term."
    title="We couldn't find anything…"
    withoutIcon
  />
);

const renderEmptyFilteredMessage = (name = 'results') => (
  <EmptyContentMessage
    className={`${CLASS_NAME}__empty-message`}
    imageSrc={emptyFilteredImage}
    subTitle="Try adjusting your filters."
    title={`No ${name} for the filters you have selected…`}
    withoutIcon
  />
);

const renderErrorMessage = () => (
  <EmptyContentMessage
    className={`${CLASS_NAME}__empty-message`}
    imageSrc={errorImage}
    subTitle="Looks like a server error, try refreshing the page."
    title="Something went wrong…"
    withoutIcon
  />
);

const renderLoading = () => (
  <div className={`${CLASS_NAME}__loading-wrapper`}>
    <Spinner size="large" />
  </div>
);

const renderInfiniteLoadingRow = () => (
  <div key={0} className={`${CLASS_NAME}__infinite-loading-row`}>
    <Spinner />
  </div>
);

const SELECTABLE_COLUMN_WIDTH = 40;
const getTableWidth = (columns, selectable) => {
  const tableWidths = _.map(columns, column => _.get(column, 'width', 0));
  return (
    _.sum(_.filter(tableWidths, tableWidth => _.isNumber(tableWidth))) +
    (selectable ? SELECTABLE_COLUMN_WIDTH : 0)
  );
};

const equalSelectedIds = (selectedIds, ids) => _.isEqual(selectedIds.sort(), ids.sort());

export class FlexTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isGreaterThanZero: false,
    };
    this.handleInfiniteScroll = this.handleInfiniteScroll.bind(this);
    this.onClearAllSelected = this.onClearAllSelected.bind(this);
    this.onClickPaginationArrow = this.onClickPaginationArrow.bind(this);
    this.onSelectAllMatching = this.onSelectAllMatching.bind(this);
    this.onSelectPage = this.onSelectPage.bind(this);
    this.renderEndMessage = this.renderEndMessage.bind(this);
    this.rowRenderer = this.rowRenderer.bind(this);
    this.onChangeScrollLeft = this.onChangeScrollLeft.bind(this);
    this.onChangeScrollLeftWithDebounce = _.debounce(this.onChangeScrollLeft, 150, {
      leading: true,
      trailing: true,
    });
  }

  onClickPaginationArrow(arrowSide = PAGINATION_ARROWS.RIGHT) {
    const { onChangePage, page } = this.props;
    if (_.isEqual(arrowSide, PAGINATION_ARROWS.RIGHT)) {
      onChangePage(page + 1);
    } else {
      onChangePage(page - 1);
    }
  }

  onChangeSelected(selectableId, value) {
    const { onChangeSelected, selectedIds } = this.props;
    const ids = value ? [...selectedIds, selectableId] : _.without(selectedIds, selectableId);
    onChangeSelected(ids);
  }

  onSelectPage(displayedIds) {
    return () => {
      const { onChangeSelected } = this.props;
      onChangeSelected(displayedIds);
    };
  }

  onChangeScrollLeft(number) {
    this.setState({ isGreaterThanZero: number > 0 });
  }

  onClearAllSelected() {
    const { onChangeSelected } = this.props;
    onChangeSelected([]);
  }

  onSelectAllMatching() {
    const { data, selectableAccessor, onChangeSelected } = this.props;
    const allMatchingIds = _.map(data, selectableAccessor);
    onChangeSelected(allMatchingIds);
  }

  handleInfiniteScroll(e) {
    const {
      fixedColumnName,
      loadingMore,
      onLoadMore,
      page,
      paginationType,
      totalPages,
    } = this.props;

    const shouldUseInfiniteLoading =
      _.isEqual(paginationType, PAGINATION_TYPES.INFINITE_SCROLL) && totalPages > 1;

    if (!isSafari && fixedColumnName)
      this.onChangeScrollLeftWithDebounce(_.get(e, 'target.scrollLeft', 0));

    if (shouldUseInfiniteLoading) {
      const scrollHeight = _.get(e, 'target.scrollHeight', 0);
      const scrollValue = _.get(e, 'target.scrollTop', 0) + _.get(e, 'target.offsetHeight', 0);
      const canLoadMore =
        totalPages > 1 && page < totalPages && !loadingMore && scrollValue > scrollHeight - 100;
      if (canLoadMore) {
        onLoadMore(page + 1);
      }
    }
  }

  rowRenderer(data, index) {
    const { columns, selectable, selectableAccessor, selectedIds, fixedColumnName } = this.props;
    const selectableId = _.get(data, selectableAccessor);
    const isSelected = _.findIndex(selectedIds, id => _.isEqual(id, selectableId)) > -1;

    return (
      <FlexAutoTableRow
        columns={columns}
        data={data}
        fixedColumnName={!isSafari && fixedColumnName}
        index={index}
        isSelected={isSelected}
        key={`item-key-${index}`}
        onChangeSelected={value => this.onChangeSelected(selectableId, value)}
        selectable={selectable}
      />
    );
  }

  renderPagination() {
    const { totalPages, page, onChangePage } = this.props;

    return (
      <Pagination
        classname={`${CLASS_NAME}__pagination`}
        currentPage={page}
        onArrowClick={this.onClickPaginationArrow}
        onPageClick={onChangePage}
        totalPagesCount={totalPages}
      />
    );
  }

  renderTableContent(data, width) {
    const {
      displayEndMessage,
      emptyMessage,
      error,
      filters,
      loading,
      loadingMore,
      name,
      page,
      paginationType,
      searchValue,
      totalPages,
      totalResults,
    } = this.props;

    if (loading) {
      return renderLoading();
    }

    if (error) {
      return renderErrorMessage();
    }

    if (totalResults < 1) {
      if (!_.isEmpty(searchValue)) {
        return renderEmptySearchedMessage();
      }

      if (_.some(_.values(filters), filter => !_.isEmpty(filter))) {
        return renderEmptyFilteredMessage(name);
      }

      const { buttonText, onClick, title, subtitle, imageSrc } = emptyMessage;
      return renderEmptyMessage({ buttonText, onClick, title, subTitle: subtitle, imageSrc });
    }

    const shouldUseInfiniteLoading =
      _.isEqual(paginationType, PAGINATION_TYPES.INFINITE_SCROLL) && totalPages > 1;
    const flexTableFilterKey = `filters-${JSON.stringify(filters)}-page-${
      shouldUseInfiniteLoading ? 'infinite' : page || 0
    }`;
    const shouldRenderLoadingRow = shouldUseInfiniteLoading && loadingMore;
    const shouldRenderEndMessage = !loadingMore && displayEndMessage;

    return (
      <>
        <div className={`${CLASS_NAME}__list`} key={flexTableFilterKey} style={{ minWidth: width }}>
          {_.map(data, this.rowRenderer)}
          {shouldRenderLoadingRow && renderInfiniteLoadingRow()}
        </div>
        {shouldRenderEndMessage && this.renderEndMessage()}
      </>
    );
  }

  renderEndMessage() {
    const { page, totalPages } = this.props;

    return (
      page === totalPages && (
        <div className={`${CLASS_NAME}__list ${CLASS_NAME}__end-message`}>
          You’ve reached the end
        </div>
      )
    );
  }

  render() {
    const {
      changeOrder,
      clearFilters,
      columns,
      data,
      filters,
      fixedColumnName,
      onChangeSelected,
      order,
      orderBy,
      paginationType,
      selectable,
      selectableAccessor,
      selectedIds,
      setFilter,
      totalPages,
      totalResults,
      unsetFilter,
    } = this.props;
    const { isGreaterThanZero } = this.state;

    const shouldUseNormalPagination = _.isEqual(paginationType, PAGINATION_TYPES.NORMAL);
    const shouldShowPagination = shouldUseNormalPagination && totalPages > 1;
    const displayedDataCount = _.size(data);
    const displayedIds = _.map(data, selectableAccessor);
    const selectedCount = _.size(selectedIds);
    const width = getTableWidth(columns, selectable);
    const shouldEqualSelected =
      !_.isEmpty(displayedIds) && _.isEqual(displayedDataCount, selectedCount);
    const allPageSelected = shouldEqualSelected
      ? equalSelectedIds(selectedIds, displayedIds)
      : false;

    const allMatchingSelected = !_.isEmpty(selectedIds) && _.isEqual(selectedCount, totalResults);
    const indeterminateSelected =
      !_.isEmpty(selectedIds) && !allPageSelected && !allMatchingSelected;

    return (
      <div
        className={classnames(CLASS_NAME, {
          '-with-shadow': isGreaterThanZero,
          '-with-sticky': !isSafari && fixedColumnName,
          '-is-edge': isEdge,
        })}
      >
        <div className={`${CLASS_NAME}__content`} onScroll={this.handleInfiniteScroll}>
          <FlexTableHeader
            allSelected={allPageSelected || allMatchingSelected}
            changeOrder={changeOrder}
            clearFilters={clearFilters}
            columns={columns}
            filters={filters}
            fixedColumnName={!isSafari && fixedColumnName}
            indeterminateSelected={indeterminateSelected}
            onChangeSelected={onChangeSelected}
            onSelectAll={this.onSelectPage(displayedIds)}
            order={order}
            orderBy={orderBy}
            selectable={selectable}
            setFilter={setFilter}
            unsetFilter={unsetFilter}
            width={width}
          />
          {this.renderTableContent(data, width)}
        </div>
        {shouldShowPagination && this.renderPagination()}
      </div>
    );
  }
}

FlexTable.propTypes = {
  changeOrder: PropTypes.func,
  clearFilters: PropTypes.func,
  columns: PropTypes.shape({}),
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.shape({})]),
  displayEndMessage: PropTypes.bool,
  emptyMessage: PropTypes.shape({ subtitle: PropTypes.string, title: PropTypes.string }),
  error: PropTypes.bool,
  filters: PropTypes.shape({}),
  fixedColumnName: PropTypes.string,
  loading: PropTypes.bool,
  loadingMore: PropTypes.bool,
  name: PropTypes.string,
  onChangePage: PropTypes.func,
  onChangeSelected: PropTypes.func,
  onLoadMore: PropTypes.func,
  order: PropTypes.string,
  orderBy: PropTypes.string,
  page: PropTypes.number,
  paginationType: PropTypes.oneOf(_.values(PAGINATION_TYPES)),
  searchValue: PropTypes.string,
  selectable: PropTypes.bool,
  selectableAccessor: PropTypes.string,
  selectedIds: PropTypes.arrayOf(PropTypes.number),
  setFilter: PropTypes.func,
  totalPages: PropTypes.number,
  totalResults: PropTypes.number,
  unsetFilter: PropTypes.func,
};

FlexTable.defaultProps = {
  data: [],
  displayEndMessage: false,
  error: false,
  loading: false,
  name: '',
  paginationType: PAGINATION_TYPES.NORMAL,
  searchValue: '',
  selectable: false,
  selectableAccessor: '',
  selectedIds: [],
};

export default FlexTable;
